import React from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { 
  Box, 
  Heading, 
  Text, 
  Flex, 
  useColorModeValue,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Button
} from '@chakra-ui/react';
import {
  FiDollarSign,
  FiCheck,
  FiActivity,
  FiAward,
  FiUsers,
  FiStar
} from 'react-icons/fi';

interface RewardsCardProps {
  // Add any props here if needed
}

// Define reward types according to documentation
const REWARD_TYPES = [
  {
    id: 'trade',
    icon: FiDollarSign,
    title: 'Trade Profit',
    description: '10% of profits from successful trades',
    color: 'green'
  },
  {
    id: 'whitelist',
    icon: FiStar,
    title: 'Whitelist Token',
    description: '$10 worth of tokens for whitelisted tokens',
    color: 'purple'
  },
  {
    id: 'strategy',
    icon: FiActivity,
    title: 'Strategy',
    description: '$10 worth of tokens for successful strategies',
    color: 'blue'
  },
  {
    id: 'governance',
    icon: FiAward,
    title: 'Governance',
    description: '$5 worth of tokens for participation',
    color: 'orange'
  },
  {
    id: 'referral',
    icon: FiUsers,
    title: 'Referral',
    description: '$5 worth of tokens for referring users',
    color: 'teal'
  }
];

const RewardsCard: React.FC<RewardsCardProps> = () => {
  const { connected } = useWallet();

  // Move all useColorModeValue calls to the top level to satisfy the rules-of-hooks
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.600');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const statBgColor = useColorModeValue('blue.50', 'rgba(118, 120, 237, 0.1)');

  // Mock data - in production this would come from an API
  const rewardStats = {
    pending: 75,
    claimed: 250,
    total: 325,
    pendingUsd: 0.34,
    claimedUsd: 1.13
  };

  return (
    <Box 
      borderWidth="1px" 
      borderRadius="lg" 
      overflow="hidden" 
      p={6} 
      bg={bgColor}
      borderColor={borderColor}
      boxShadow="sm"
    >
      <Heading size="md" mb={4} textAlign="left" color={textColor}>Rewards System</Heading>
      
      {!connected ? (
        <Alert status="info" borderRadius="md" mb={4}>
          <AlertIcon />
          <Flex direction="column">
            <Text fontSize="sm">Connect your wallet to view and claim rewards</Text>
          </Flex>
        </Alert>
      ) : (
        <>
          {/* Reward Stats */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb={6}>
            <Stat bg={statBgColor} p={3} borderRadius="md">
              <StatLabel>Pending Rewards</StatLabel>
              <StatNumber>{rewardStats.pending} DESOL</StatNumber>
              <StatHelpText>${rewardStats.pendingUsd}</StatHelpText>
            </Stat>
            
            <Stat bg={statBgColor} p={3} borderRadius="md">
              <StatLabel>Claimed Rewards</StatLabel>
              <StatNumber>{rewardStats.claimed} DESOL</StatNumber>
              <StatHelpText>${rewardStats.claimedUsd}</StatHelpText>
            </Stat>
            
            <Flex 
              direction="column"
              justify="center"
              align="center"
              bg={statBgColor}
              p={3}
              borderRadius="md"
            >
              <Button 
                leftIcon={<FiCheck />}
                colorScheme="blue"
                size="md"
                isDisabled={rewardStats.pending <= 0}
              >
                Claim Rewards
              </Button>
              <Text fontSize="xs" mt={2} color={secondaryTextColor}>
                Min claim: 5 DESOL
              </Text>
            </Flex>
          </SimpleGrid>
        </>
      )}
      
      {/* Rewards Types - Always show regardless of wallet connection */}
      <Box mb={6}>
        <Heading size="sm" mb={3} color={textColor}>Reward Types</Heading>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
          {REWARD_TYPES.map((type) => (
            <Box 
              key={type.id}
              p={4} 
              bg={cardBgColor} 
              borderRadius="md" 
              borderWidth="1px" 
              borderColor={cardBorderColor}
            >
              <Flex align="center" mb={2}>
                <Icon as={type.icon} color={`${type.color}.400`} boxSize={5} mr={2} />
                <Text fontWeight="bold" color={textColor}>{type.title}</Text>
              </Flex>
              <Text fontSize="sm" color={secondaryTextColor}>
                {type.description}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default RewardsCard;